/* HeroTour — the /v2 hero merge: the hero itself pins while the visitor's
   scroll flips 5 app screenshots inside the Apple iPhone 17 bezel. The
   hero copy (h1 / lede / App Store badge) is passed in from LandingPage so
   it stays the SAME node tree as the real homepage hero — no forked copy.
   Beat text is deliberately demoted to a small mono caption under the
   phone so it never competes with the hero headline.

   Derived from ProductTour.tsx (the QA'd 13-screen section variant, kept
   for reference). Deck geometry and the 0.7s swipe are identical; the pin
   is shorter: 80vh of scroll per screen instead of 100vh, because a hero
   pin delays the rest of the page and earns less dwell than a mid-page
   section. Tune VH_PER_SCREEN to taste.

   Assets: public/tour/ (see ProductTour.tsx notes on updating them). */

import { useEffect, useRef, useState } from 'react';
import './hero-tour.css';

type HeroScreen = { src: string; eyebrow: string; caption: string };

/* 6 screens: protocol, a 3-step guided-mixing sequence (the wedge), library,
   tracking. Real app captures at 760x1653 (native 1206x2622 downscaled).
   Eyebrow says "Guided mixing" (ICP word) to match the app's "MIX YOUR
   PEPTIDE" flow, not the old "reconstitution".
   Screen 1 shows the Tirzepatide "your protocol, simplified" card. Chosen
   over the original reta capture: retatrutide + a specific dose on a public
   marketing surface is a live compliance risk (see marketing context §11-12);
   Tirzepatide is an approved GLP-1, safe to show, and the step-up dosing it
   displays reinforces the "raise your dose step by step" feature card. */
const SCREENS: HeroScreen[] = [
  { src: '/tour/screens/protocol-tirz.webp', eyebrow: 'Plain English', caption: 'Your protocol, simplified.' },
  { src: '/tour/screens/r1.webp', eyebrow: 'Guided mixing', caption: 'Start with what you have.' },
  { src: '/tour/screens/mix-wall.webp', eyebrow: 'Guided mixing', caption: 'Down the wall, nice and slow.' },
  { src: '/tour/screens/mix-roll.webp', eyebrow: 'Guided mixing', caption: 'No guesswork. No shaking.' },
  { src: '/tour/screens/l1.webp', eyebrow: 'The library', caption: 'Research every compound and stack.' },
  { src: '/tour/screens/d1.webp', eyebrow: 'Tracking', caption: 'Never lose track of your doses.' },
];

const VH_PER_SCREEN = 80;

/* Phones and tablets get NO pin. On a touch screen the pinned hero held the
   headline still for ~5 screen-heights of swiping, which read as a frozen
   page (2026-09-10). Below this width the hero scrolls like any section and
   the deck plays itself once instead. Keep in sync with the
   max-width: 1024px block in hero-tour.css. */
const UNPINNED_QUERY = '(max-width: 1024px)';

/* Phones show copy only - no phone, no caption (Lawrence, 2026-09-10): at
   this size a half-swiping deck read as a strange piece of UI, and the hero
   lands harder as headline, lede, CTA and the funnel link with room to
   breathe. Keep in sync with the max-width: 640px block in hero-tour.css. */
const PHONE_QUERY = '(max-width: 640px)';
const AUTO_ADVANCE_MS = 3200;

const matches = (query: string) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches;

export default function HeroTour({ copy }: { copy: React.ReactNode }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [gi, setGi] = useState(0);
  const [pinned, setPinned] = useState(() => !matches(UNPINNED_QUERY));
  const [showStage, setShowStage] = useState(() => !matches(PHONE_QUERY));
  const [inView, setInView] = useState(true);

  // Follow both breakpoints (a phone rotating, an iPad crossing 1024px).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const unpinned = window.matchMedia(UNPINNED_QUERY);
    const phone = window.matchMedia(PHONE_QUERY);
    const onChange = () => {
      setPinned(!unpinned.matches);
      setShowStage(!phone.matches);
    };
    unpinned.addEventListener('change', onChange);
    phone.addEventListener('change', onChange);
    return () => {
      unpinned.removeEventListener('change', onChange);
      phone.removeEventListener('change', onChange);
    };
  }, []);

  // Unpinned: advance only while the hero is on screen.
  useEffect(() => {
    if (pinned || !showStage) return;
    const el = trackRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [pinned, showStage]);

  // Unpinned: one pass through the deck, then rest on the last screen.
  // Reduced motion keeps the first screen still.
  useEffect(() => {
    if (pinned || !showStage || !inView || gi >= SCREENS.length - 1) return;
    if (matches('(prefers-reduced-motion: reduce)')) return;
    const t = window.setTimeout(() => setGi(gi + 1), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [pinned, showStage, inView, gi]);

  // Pinned (desktop): scroll progress through the track picks the screen.
  useEffect(() => {
    if (!pinned) return;
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const read = () => {
      const r = track.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, -r.top / (r.height - vh)));
      setGi(Math.min(SCREENS.length - 1, Math.floor(p * SCREENS.length)));
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(read); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    read();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [pinned]);

  return (
    <section className="pv2-hero">
      <div
        className="ht-track"
        ref={trackRef}
        style={pinned ? { height: `${SCREENS.length * VH_PER_SCREEN + 100}vh` } : undefined}
      >
        <div className="ht-pin">
          <div className="ht-grid">
            <div className="ht-copy">{copy}</div>
            {showStage && (
              <div className="ht-stage">
                <div className="ht-phone">
                  <div className="ht-phone-screen">
                    {SCREENS.map((s, k) => (
                      // Plain <img>: absolutely-stacked, transform-animated
                      // siblings — wrappers would break the deck layering.
                      <img
                        key={s.src}
                        className={`ht-screen${k === gi ? ' is-current' : ''}${k < gi ? ' is-past' : ''}`}
                        src={s.src}
                        alt=""
                        decoding="async"
                        loading={k > 1 ? 'lazy' : 'eager'}
                      />
                    ))}
                  </div>
                  <img className="ht-phone-bezel" src="/tour/iphone17.webp" alt="" />
                </div>
                <div className="ht-caption">
                  {SCREENS.map((s, k) => (
                    <div key={k} className={`ht-cap${k === gi ? ' is-active' : ''}`}>
                      <span className="ht-cap-meta">
                        <span className="ht-cap-count">{`0${k + 1} / 0${SCREENS.length}`}</span>
                        <span className="ht-cap-eyebrow">{s.eyebrow}</span>
                      </span>
                      <span className="ht-cap-line">{s.caption}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
